import { useId, useMemo, useState } from "react";
import { Button, FormControlLabel, Switch, Tooltip } from "@mui/material";

import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import {
    ChatBot,
    ChatPlatform,
    UpdateChatBotRequest
} from "../../../common/interfaces/admin/chat-bot.interface";

interface Props {
    open: boolean;
    bot: ChatBot;
    onCancel: () => void;
    onSubmit: (data: UpdateChatBotRequest) => Promise<void>;
}

const platformLabel: Record<string, string> = {
    [ChatPlatform.Cliq]: "Zoho Cliq",
    [ChatPlatform.Teams]: "Microsoft Teams",
    [ChatPlatform.Slack]: "Slack",
    [ChatPlatform.GoogleChat]: "Google Chat"
};

const EditChatBotModal = ({ open, bot, onCancel, onSubmit }: Props) => {
    const [name, setName] = useState(bot.name);
    const [isActive, setIsActive] = useState(bot.isActive);
    const [signingSecret, setSigningSecret] = useState("");
    const [microsoftAppId, setMicrosoftAppId] = useState("");
    const [microsoftAppPassword, setMicrosoftAppPassword] = useState("");
    const [tenantId, setTenantId] = useState("");
    const [googleProjectNumber, setGoogleProjectNumber] = useState("");
    const [authenticationAudience, setAuthenticationAudience] = useState("");
    const [googleServiceAccountJson, setGoogleServiceAccountJson] = useState("");
    const [slackSigningSecret, setSlackSigningSecret] = useState("");
    const [slackBotToken, setSlackBotToken] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string>("");
    // Base id for associating <label htmlFor> with each field's control (WCAG 1.3.1/3.3.2).
    const fieldId = useId();

    const isTeams = bot.platform === ChatPlatform.Teams;
    const isGoogleChat = bot.platform === ChatPlatform.GoogleChat;
    const isSlack = bot.platform === ChatPlatform.Slack;

    const validation = useMemo(() => {
        const issues: string[] = [];
        if (!name.trim()) issues.push("Name is required");
        return { isValid: issues.length === 0, issues };
    }, [name]);

    const handleSubmit = async () => {
        if (!validation.isValid) return;
        setSubmitting(true);
        setServerError("");
        try {
            const credentials: Record<string, string> = {};
            if (isTeams) {
                if (microsoftAppId.trim()) credentials.microsoftAppId = microsoftAppId.trim();
                if (microsoftAppPassword.trim())
                    credentials.microsoftAppPassword = microsoftAppPassword.trim();
                if (tenantId.trim()) credentials.tenantId = tenantId.trim();
            } else if (isGoogleChat) {
                if (googleProjectNumber.trim())
                    credentials.googleProjectNumber = googleProjectNumber.trim();
                if (authenticationAudience.trim())
                    credentials.authenticationAudience = authenticationAudience.trim();
                if (googleServiceAccountJson.trim()) {
                    try {
                        const parsed = JSON.parse(googleServiceAccountJson);
                        if (!parsed.client_email || !parsed.private_key) {
                            setServerError(
                                "Service Account JSON must include client_email and private_key"
                            );
                            setSubmitting(false);
                            return;
                        }
                    } catch {
                        setServerError("Service Account JSON is not valid JSON");
                        setSubmitting(false);
                        return;
                    }
                    credentials.serviceAccountJson = googleServiceAccountJson.trim();
                }
            } else if (isSlack) {
                if (slackSigningSecret.trim())
                    credentials.signingSecret = slackSigningSecret.trim();
                if (slackBotToken.trim()) credentials.botToken = slackBotToken.trim();
            } else {
                if (signingSecret.trim()) credentials.signingSecret = signingSecret.trim();
            }

            const req: UpdateChatBotRequest = {
                name: name.trim(),
                isActive
            };
            if (Object.keys(credentials).length > 0) req.credentials = credentials;

            await onSubmit(req);
        } catch (err: any) {
            setServerError(err?.response?.data?.message || "Failed to update bot");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalTemplate open={open} width={500} handleClose={onCancel} ariaLabel="Edit chat bot">
            <div className="chat-bot-form">
                <h2 className="chat-bot-form__title">
                    Edit {platformLabel[bot.platform] ?? bot.platform} Bot
                </h2>
                <div className="chat-bot-form__intro">
                    Credential fields are blank and optional &mdash; leave blank to keep the stored
                    value, or enter a new value to replace it. Saved credentials are never
                    displayed.
                </div>

                <div className="chat-bot-form__field">
                    <label htmlFor={`${fieldId}-name`}>Name</label>
                    <input
                        id={`${fieldId}-name`}
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </div>

                <div className="chat-bot-form__field chat-bot-form__field--inline">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isActive}
                                onChange={e => setIsActive(e.target.checked)}
                            />
                        }
                        label={isActive ? "Active" : "Inactive"}
                    />
                </div>

                {isTeams ? (
                    <>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-msAppId`}>Microsoft App ID</label>
                            <input
                                id={`${fieldId}-msAppId`}
                                type="text"
                                value={microsoftAppId}
                                onChange={e => setMicrosoftAppId(e.target.value)}
                                placeholder="Leave blank to keep current"
                            />
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-msAppPassword`}>
                                Microsoft App Password
                            </label>
                            <input
                                id={`${fieldId}-msAppPassword`}
                                type="password"
                                value={microsoftAppPassword}
                                onChange={e => setMicrosoftAppPassword(e.target.value)}
                                placeholder="Leave blank to keep current"
                                autoComplete="off"
                            />
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-tenantId`}>Tenant ID</label>
                            <input
                                id={`${fieldId}-tenantId`}
                                type="text"
                                value={tenantId}
                                onChange={e => setTenantId(e.target.value)}
                                placeholder="Leave blank to keep current"
                            />
                        </div>
                    </>
                ) : isGoogleChat ? (
                    <>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-gcpProjectNumber`}>
                                Google Cloud Project Number
                            </label>
                            <input
                                id={`${fieldId}-gcpProjectNumber`}
                                type="text"
                                value={googleProjectNumber}
                                onChange={e => setGoogleProjectNumber(e.target.value)}
                                placeholder="Leave blank to keep current"
                            />
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-authAudience`}>
                                Authentication Audience
                            </label>
                            <input
                                id={`${fieldId}-authAudience`}
                                type="text"
                                value={authenticationAudience}
                                onChange={e => setAuthenticationAudience(e.target.value)}
                                placeholder="Leave blank to keep current"
                            />
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-serviceAccountJson`}>
                                Service Account JSON Key
                            </label>
                            <textarea
                                id={`${fieldId}-serviceAccountJson`}
                                rows={6}
                                value={googleServiceAccountJson}
                                onChange={e => setGoogleServiceAccountJson(e.target.value)}
                                placeholder="Leave blank to keep current"
                                aria-describedby={`${fieldId}-serviceAccountJson-help`}
                            />
                            <span
                                id={`${fieldId}-serviceAccountJson-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Paste a new JSON key to rotate. Must have the <b>Chat Bot</b> role.
                            </span>
                        </div>
                    </>
                ) : isSlack ? (
                    <>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-signingSecret`}>Signing Secret</label>
                            <input
                                id={`${fieldId}-signingSecret`}
                                type="password"
                                value={slackSigningSecret}
                                onChange={e => setSlackSigningSecret(e.target.value)}
                                placeholder="Leave blank to keep current"
                                autoComplete="off"
                                aria-describedby={`${fieldId}-signingSecret-help`}
                            />
                            <span
                                id={`${fieldId}-signingSecret-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Slack App &rarr; Basic Information &rarr; Signing Secret
                            </span>
                        </div>
                        <div className="chat-bot-form__field">
                            <label htmlFor={`${fieldId}-botUserToken`}>Bot User OAuth Token</label>
                            <input
                                id={`${fieldId}-botUserToken`}
                                type="password"
                                value={slackBotToken}
                                onChange={e => setSlackBotToken(e.target.value)}
                                placeholder="Leave blank to keep current (xoxb-...)"
                                autoComplete="off"
                                aria-describedby={`${fieldId}-botUserToken-help`}
                            />
                            <span
                                id={`${fieldId}-botUserToken-help`}
                                className="chat-bot-form__field-helper"
                            >
                                Slack App &rarr; OAuth &amp; Permissions &rarr; Bot User OAuth Token
                            </span>
                        </div>
                    </>
                ) : (
                    <div className="chat-bot-form__field">
                        <label htmlFor={`${fieldId}-apiKey`}>API Key</label>
                        <input
                            id={`${fieldId}-apiKey`}
                            type="password"
                            value={signingSecret}
                            onChange={e => setSigningSecret(e.target.value)}
                            placeholder="Leave blank to keep current"
                            autoComplete="off"
                            aria-describedby={`${fieldId}-apiKey-help`}
                        />
                        <span id={`${fieldId}-apiKey-help`} className="chat-bot-form__field-helper">
                            The x-api-key header value Cliq sends on each webhook.
                        </span>
                    </div>
                )}

                {serverError && (
                    <div className="chat-bot-form__error" role="alert">
                        {serverError}
                    </div>
                )}

                <div className="chat-bot-form__actions">
                    <Button
                        className="chat-bot-form__cancel"
                        variant="text"
                        onClick={onCancel}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Tooltip
                        title={
                            !validation.isValid ? (
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {validation.issues.map((issue, i) => (
                                        <li key={i}>{issue}</li>
                                    ))}
                                </ul>
                            ) : (
                                ""
                            )
                        }
                        disableHoverListener={validation.isValid}
                    >
                        <span>
                            <Button
                                className="chat-bot-form__submit"
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting || !validation.isValid}
                            >
                                {submitting ? "Saving..." : "Save"}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default EditChatBotModal;
